import Box from '@mui/material/Box'
import Typography from '@mui/material/Typography'
import Markdown from '../Markdown'
import { bodyText, colours } from '../player/playerStyles'

const SubHeading = ({ children }) => (
  <Typography component="h3" sx={{ fontSize: '1.125rem', fontWeight: 700, mt: 3, mb: 1 }}>
    {children}
  </Typography>
)

const Fact = ({ label, children }) => (
  <Box sx={{ mb: 1.5 }}>
    <Box component="dt" sx={{ fontWeight: 700 }}>
      {label}
    </Box>
    <Box component="dd" sx={{ m: 0 }}>
      {children}
    </Box>
  </Box>
)

// Built from the course and pathway records; the block itself supplies "why
// this matters". Course screens carry no organisation name or logo: the
// course is AmbuNet's, not the organisation's.
const CourseIntro = ({ block, content }) => {
  const course = content?.course ?? {}
  const pathway = content?.pathway ?? {}

  return (
    <Box sx={{ maxWidth: '74ch' }}>
      <Typography sx={{ fontSize: '1.375rem', fontWeight: 700, lineHeight: 1.3, mb: 0.5 }}>{course.title}</Typography>
      {/* Only worth showing when the course has more than one level. */}
      {pathway.title && (course.pathways?.length ?? 0) > 1 && (
        <Typography sx={{ fontSize: '1.0625rem', color: colours.muted, mb: 2 }}>{pathway.title}</Typography>
      )}

      <SubHeading>Why this matters</SubHeading>
      <Markdown sx={bodyText}>{block.body}</Markdown>

      {course.learningObjectives?.length > 0 && (
        <>
          <SubHeading>By the end of this course you will be able to:</SubHeading>
          <Box component="ul" sx={{ ...bodyText, m: 0, pl: 3 }}>
            {course.learningObjectives.map((objective, index) => (
              <Box component="li" key={index} sx={{ mb: 0.75 }}>
                {objective.text}
              </Box>
            ))}
          </Box>
        </>
      )}

      <SubHeading>Before you start</SubHeading>
      <Box component="dl" sx={{ ...bodyText, m: 0 }}>
        {pathway.estimatedMinutes > 0 && <Fact label="Time needed">About {pathway.estimatedMinutes} minutes</Fact>}
        {course.passMark > 0 && (
          <Fact label="Pass mark">
            The course ends with an assessment. You need {course.passMark}% to pass.
          </Fact>
        )}
        {course.retakePolicy && <Fact label="Retakes">{course.retakePolicy}</Fact>}
        <Fact label="Your progress">
          Your progress saves as you go. You can stop at any point and carry on later from where you left off.
        </Fact>
      </Box>

      {course.framework?.statement && (
        <>
          <SubHeading>Framework</SubHeading>
          <Markdown sx={bodyText}>{course.framework.statement}</Markdown>
        </>
      )}

      <SubHeading>Your information</SubHeading>
      <Typography sx={bodyText}>
        Your progress, answers, reflections and results are stored for your organisation. You and your
        organisation&apos;s training administrators can see them.{' '}
        <Box
          component="a"
          href="/privacy-policy"
          target="_blank"
          rel="noopener noreferrer"
          sx={{ color: colours.accent, textDecoration: 'underline' }}
        >
          Read the privacy policy (opens in a new tab)
        </Box>
      </Typography>
    </Box>
  )
}

export default CourseIntro
